import type { Contributor, UserPermissionType } from '../event-permissions';

import {
  IconButton,
  Paragraph,
  Spinner,
  Tooltip,
  TrashIcon,
  notification,
  toast,
} from '@movingimage-evp/mi-ui-component-library';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import {
  GetWebcastPermissionsDocument as GET_EVENT_PERMISSIONS,
  ItemListAction,
  useManageEventPermissionsMutation,
} from 'src/generated/graphql-manager';
import { useCurrentUser } from 'src/manager/hooks/current-user';
import { useUserPermissions } from 'src/manager/hooks/user-permissions';

import { Search } from '../../../../components/search';

import styles from './contributors-list.module.css';

export function ContributorsList({ contributors }: { contributors: Contributor[] }) {
  const { webcastId = '' } = useParams();
  const { userId, isAdminUser } = useCurrentUser();
  const { isEventEditingAllowed } = useUserPermissions();
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [deleteContributorObject, setDeleteContributorObject] = useState<{
    id: Contributor['id'];
    type: Contributor['type'];
  } | null>(null);

  const [deleteContributor, { loading: deleteLoading }] = useManageEventPermissionsMutation();

  const handleDeleteContributor = (contributorId: Contributor['id'], type: Contributor['type']) => {
    setDeleteContributorObject({ id: contributorId, type });

    deleteContributor({
      variables: {
        input: {
          webcastId,
          [type]: {
            items: [contributorId],
            action: ItemListAction.REMOVE,
          },
        },
      },
      refetchQueries: [{ query: GET_EVENT_PERMISSIONS, variables: { webcastId } }],
      onCompleted: (data) => {
        if (data?.updateEventPermission.__typename === 'UpdateEventPermissionSuccess') {
          toast(t('manager.webcastSetup.toast.saveSuccess'), 'positive');
        } else {
          notification(t('manager.webcastSetup.toast.saveError'), 'negative');
        }

        setDeleteContributorObject(null);
      },
      onError: () => {
        notification(t('manager.webcastSetup.toast.saveError'), 'negative');
      },
    });
  };

  if (contributors.length === 0) return null;

  const filteredUsers = contributors.filter(
    (user) =>
      user.firstName?.toLowerCase().includes(search.toLowerCase()) ||
      user.lastName?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase())
  );

  const mergedContributors = filteredUsers
    .reduce(
      (users, user) => {
        const existingUser = users.find(({ id }) => id === user.id);

        if (existingUser) existingUser.types.push(user.type);
        else users.push({ ...user, types: [user.type] });

        return users;
      },
      [] as (Contributor & { types: Contributor['type'][] })[]
    )
    .sort((a, b) => {
      const userA = (a.firstName || a.lastName || a.email || '').toLowerCase();
      const userB = (b.firstName || b.lastName || b.email || '').toLowerCase();

      if (userA < userB) return -1;
      if (userA > userB) return 1;
      return 0;
    });

  const isOnlyOneOwner = contributors.filter((contributor) => contributor.type === 'owners').length === 1;
  const isLoginUserOwner = contributors.some(
    (contributor) => contributor.type === 'owners' && contributor.id === userId
  );

  const enableDeleteButton = (user: Contributor, permissionType: UserPermissionType) => {
    if ((isOnlyOneOwner && permissionType === 'owners') || !isEventEditingAllowed) return false;
    if (isAdminUser) return true;

    if (user.id === userId) return permissionType === 'collaborators';

    return isLoginUserOwner;
  };

  const getDeleteContributorText = (userType: Contributor['type']) => {
    return isOnlyOneOwner && userType === 'owners'
      ? t('manager.webcastSetup.eventPermissions.options.private.onlyOwner')
      : t('manager.webcastSetup.eventPermissions.options.private.removeButton');
  };
  return (
    <div data-testid="contributors-list" className={styles.wrapper}>
      {contributors?.length > 10 && (
        <Search
          placeholder={t('manager.webcastSetup.eventPermissions.options.private.searchPlaceholder')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          onClear={() => setSearch('')}
        />
      )}

      <div data-testid="contributors-list-items" className={styles.items}>
        {mergedContributors.map((user, index) => (
          <div key={index} className={styles.item} data-testid="contributor-item">
            <span data-testid={`contributor-${index}-label`}>
              {user.firstName || user.lastName ? `${user.firstName} ${user.lastName}` : user.email}
            </span>

            <div className={styles.roles}>
              {user.types.map((type) => (
                <div key={type} className={styles.element}>
                  <span data-testid={`contributor-${index}-${type}-role`}>
                    {t(`manager.webcastSetup.eventPermissions.options.private.${type}`)}
                  </span>

                  {(isAdminUser || isLoginUserOwner) && (
                    <Tooltip label={getDeleteContributorText(type)}>
                      <IconButton
                        data-testid={`contributor-${index}-${type}-remove-button`}
                        type="button"
                        aria-label={getDeleteContributorText(type)}
                        rounded
                        disabled={!enableDeleteButton(user, type)}
                        onClick={() => handleDeleteContributor(user.id, type)}
                      >
                        {deleteLoading &&
                        deleteContributorObject?.id === user.id &&
                        deleteContributorObject?.type === type ? (
                          <Spinner size={16} />
                        ) : (
                          <TrashIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        {mergedContributors.length === 0 && (
          <Paragraph data-testid="contributors-no-results">
            {t('manager.webcastSetup.eventPermissions.options.private.noResults')}
          </Paragraph>
        )}
      </div>
    </div>
  );
}
